import React, { useState, useEffect, FunctionComponent } from "react"
import { IconContext } from "react-icons/lib"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { ContactListPaginated } from "./components/ContactListPaginated"
import EditContact from "./components/EditContact"
import SearchBar from "@murphy-frontend/web-core/components/SearchBar"
import { useGetContacts, useGetCustomerContactOptionSets } from "../../common/api/Contacts/queries"
import { deleteContactMutation } from "../../common/api/Contacts/mutations"
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { ContactModel, ContactViewModel, OptionSetModel, OptionSetViewModel } from "../../common/api/Contacts/ContactsApi"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { useCustomer } from "../../common/contexts/CustomerContext"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import Button from "@murphy-frontend/web-core/components/Button"
import { AiOutlinePlus } from "react-icons/ai"

const Contacts: FunctionComponent = () => {
	const { customer } = useCustomer()
	const { translations } = useUserPreferences()
	const { openModal, closeModal } = useModal()

	const [error, setError] = useState(null)
	const [contacts, setContacts] = useState<ContactViewModel[]>([])
	const [optionSets, setOptionSets] = useState<OptionSetViewModel[]>([]);

	const [filteredContacts, setFilteredContacts] = useState<ContactViewModel[]>([])
	const [currentSearchText, setCurrentSearchText] = useState("")

	const {
		isLoading: isLoadingContacts,
		data: dataContacts
	} = useGetContacts(customer?.Id)

	const {
		isLoading: isLoadingCustomerContactOptionSets,
		data: dataCustomerContactOptionSets
	} = useGetCustomerContactOptionSets(customer?.Id)

	const {
		mutate: delMutate,
		isPending: deleteIsLoading
	} = deleteContactMutation(translations)

	const createContactViewModels = (data: ContactModel[]): ContactViewModel[] => {
		const mappedData = data.map(p => ({
			name: p.Name,
			id: p.ID,
			email: p.Email,
			mobile: p.MobileNr,
			type: "contact",
			selectedOptions: p.SelectedOptions.map(q => ({
				id: q.ID,
				value: q.Value,
				optionSetId: q.OptionSetId,
				optionSetName: q.OptionSetName
			}))
		}))

		mappedData.sort((a, b) => a?.name?.localeCompare(b.name))

		return mappedData
	}

	const onGetOptionSetSuccess = (data: OptionSetModel[]): void => {
		const mappedData: OptionSetViewModel[] = data.map(p => ({
			id: p.ID,
			name: p.Name,
			options: p.Options.map(q => ({
				id: q.ID,
				value: q.Value
			}))
		}))

		mappedData.sort((a, b) => a.name.localeCompare(b.name))
		setOptionSets(mappedData)
	}

	function ErrorMessage() {
		return <p>{error}</p>
	}

	function DeleteConfirmationBody({ name }: { name: string }) {
		return (
			<p>
				{translations.delete} <strong>{name}</strong> ?
			</p>
		)
	}

	const onClickNewContact = () => {
		const modalConf: ModalConfiguration = {
			title: translations.newcontact,
			body: (
				<EditContact
					translations={translations}
					customerId={customer.Id}
					optionSets={optionSets}
					isEditing={false}
					onCancel={() => closeModal()}
					onSuccess={() => closeModal()}
					onFail={() => closeModal()}
					title={translations.addcontact || "Add new contact"}
				/>
			),
			hideButton: true,
			type: "info",
			position: "center"
		}

		openModal(modalConf)
	}

	const onClickEditContact = (contactId: number) => {
		const contactToEdit = contacts.filter(p => p.id === contactId)[0]

		const modalConf: ModalConfiguration = {
			title: translations.updatecontact,
			body: (
				<EditContact
					translations={translations}
					customerId={customer.Id}
					contactId={contactToEdit.id}
					nameInit={contactToEdit.name}
					emailInit={contactToEdit.email}
					mobileInit={contactToEdit.mobile}
					optionSets={optionSets}
					title={translations.editcontact || "Edit contact"}
					selectedOptions={contactToEdit.selectedOptions}
					isEditing
					onCancel={() => closeModal()}
					onSuccess={() => closeModal()}
					onFail={() => closeModal()}
				/>
			),
			hideButton: true,
			type: "info",
			position: "center"
		}

		openModal(modalConf)
	}

	const onClickDeleteContact = (contactId: number) => {
		const contactToEdit = contacts.filter(p => p.id === contactId)[0]
		const modalConf: ModalConfiguration = {
			title: translations.deletecontact,
			body: <DeleteConfirmationBody name={contactToEdit.name} />,
			hideButton: false,
			buttonText: translations.delete,
			type: "warning",
			position: "center",
			okCallback: () => delMutate(contactToEdit.id)
		}

		openModal(modalConf)
	}

	useEffect(() => {
		if (!currentSearchText) {
			setFilteredContacts([...contacts]);
		} else {
			const searchValue = currentSearchText.toUpperCase()
			const newFilteredContacts = contacts.filter(p => p.name.toUpperCase().startsWith(searchValue))
			setFilteredContacts(newFilteredContacts)
		}
	}, [currentSearchText])

	useEffect(() => {
		if (isLoadingContacts === false) {
			const contactViewModels = createContactViewModels(dataContacts)
			setFilteredContacts(contactViewModels)
			setContacts(contactViewModels)
		}
	}, [dataContacts])

	useEffect(() => {
		if (isLoadingCustomerContactOptionSets === false) {
			onGetOptionSetSuccess(dataCustomerContactOptionSets)
		}
	}, [dataCustomerContactOptionSets])

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	if (isLoadingContacts || isLoadingCustomerContactOptionSets || deleteIsLoading) {
		return (
			<ComponentWrapper active={2} subMenuActive={3}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	return (
		<ComponentWrapper active={2} subMenuActive={3}>
			<div style={{ display: 'flex', paddingLeft: 20, paddingRight: 20, marginTop: 20, flexDirection: 'column', width: '90rem' }}>
				<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '80rem', alignItems: 'center' }}>
					<h1>{translations.contacts}</h1>
					<div onClick={onClickNewContact} style={{ cursor: 'pointer', height: 40, width: '14rem', display: 'flex', borderWidth: 1, flexDirection: 'row', backgroundColor: '#FFFFFF', borderColor: '#00B2A9', borderStyle: 'solid', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingBottom: 10, paddingTop: 10, borderRadius: 5 }}>
						<IconContext.Provider value={{ className: 'btn-icon' }}>
							<AiOutlinePlus color="#00B2A9" />
						</IconContext.Provider>
						<span style={{ marginLeft: 10, color: "#00B2A9" }}>{translations.addcontact || "Add new contact"}</span>
					</div>
				</div>

				<div className="three" style={{ display: 'flex', width: '80rem', marginTop: 20 }}>
					<div style={{ width: '80rem' }}>
						<SearchBar
							val={currentSearchText}
							handleChange={(event) => setCurrentSearchText(event.target.value)}
							placeholder={translations["placeholder-lang-search-action"]}
							showSearchIcon={true}
						/>
					</div>
				</div>

				<div style={{ width: '80rem' }}>
					<ContactListPaginated
						contacts={filteredContacts}
						optionSets={optionSets}
						translations={translations}
						onClickEdit={onClickEditContact}
						onClickDelete={onClickDeleteContact}
					/>
				</div>
			</div>
		</ComponentWrapper>
	)
}

export default Contacts